// pages/app/Settings.tsx — theme, Key1/Key2, export/delete/kill-switch, audit (spec §4.4, §8, §2.4).
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useServices } from '../../app/AppContext';
import { useSetupContext } from '../../app/SetupContext';
import { useTheme } from '../../hooks/useTheme';
import { THEMES, themeLabel, type Theme } from '../../lib/theme';
import { ConfirmDialog } from '../../components/Dialog';
import { MonitorIcon, MoonIcon, SunIcon } from '../../components/icons';

const KEY1_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  claude: 'Claude (Anthropic)',
  github_copilot_oauth: 'GitHub Copilot',
};
const THEME_ICON: Record<Theme, typeof SunIcon> = {
  system: MonitorIcon,
  light: SunIcon,
  dark: MoonIcon,
};

function downloadJson(name: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function Settings() {
  const { api } = useServices();
  const { status, refresh } = useSetupContext();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [k2provider, setK2provider] = useState('openai_realtime');
  const [k2endpoint, setK2endpoint] = useState('');
  const [k2model, setK2model] = useState('');
  const [k2key, setK2key] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<Record<string, unknown>[] | null>(null);
  const [confirming, setConfirming] = useState<null | 'persona' | 'all'>(null);

  const isAzure = k2provider === 'azure_foundry';

  function report(fn: () => Promise<string>) {
    return async () => {
      setBusy(true);
      setError(null);
      setMessage(null);
      try {
        setMessage(await fn());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Action failed');
      } finally {
        setBusy(false);
      }
    };
  }

  async function saveKey2(e: FormEvent) {
    e.preventDefault();
    await report(async () => {
      await api.setKey2({
        provider: k2provider,
        apiKey: k2key,
        model: k2model || undefined,
        endpoint: isAzure ? k2endpoint : undefined,
      });
      await refresh();
      setK2key('');
      return 'Key2 saved — live voice chat enabled.';
    })();
  }

  const doExport = (scope: string) =>
    report(async () => {
      const data = await api.exportData(scope);
      downloadJson(`upload-${scope}.json`, data);
      return `Exported ${scope}.`;
    });

  const runDelete = (kind: 'persona' | 'all') =>
    report(async () => {
      setConfirming(null);
      if (kind === 'persona') {
        await api.deletePersona();
        await refresh();
        return 'Persona deleted.';
      }
      await api.deleteAll();
      await refresh();
      return 'All data destroyed.';
    });

  const loadAudit = report(async () => {
    const r = await api.getAudit();
    setAudit(r.entries);
    return `Loaded ${r.entries.length} audit entries.`;
  });

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6">
      {message ? (
        <p
          role="status"
          className="flex items-center gap-2 rounded-xl border border-success/20 bg-success/10 px-3.5 py-2.5 text-sm text-success"
        >
          {message}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
        >
          {error}
        </p>
      ) : null}

      {/* Key1 summary */}
      <section className="card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Key1 — your LLM</h2>
            <p className="mt-1 text-sm text-fg-muted">Powers extraction, refinement, and chat.</p>
          </div>
          <button type="button" className="btn-ghost" onClick={() => navigate('/app/setup')}>
            Reconfigure
          </button>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ['Provider', KEY1_LABELS[status?.key1Provider ?? ''] ?? status?.key1Provider ?? '—'],
            ['Model', status?.model ?? '—'],
            ['Reasoning', status?.reasoning ?? '—'],
            ['Context', status?.contextWindow ? status.contextWindow.toLocaleString() : '—'],
          ].map(([label, value]) => (
            <div key={label} className="card-inset">
              <dt className="text-xs text-fg-subtle">{label}</dt>
              <dd className="mt-0.5 truncate text-sm font-medium text-fg">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* Theme */}
      <section className="card">
        <h2 className="text-base font-semibold">Appearance</h2>
        <p className="mt-1 text-sm text-fg-muted">Choose how UPLOAD looks.</p>
        <div className="mt-3 inline-flex rounded-xl border border-line/12 bg-surface-2 p-1">
          {THEMES.map((t: Theme) => {
            const Icon = THEME_ICON[t];
            const activeTheme = t === theme;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTheme(t)}
                aria-pressed={activeTheme}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTheme ? 'bg-surface text-fg shadow-xs' : 'text-fg-muted hover:text-fg'
                }`}
              >
                <Icon className="h-4 w-4" />
                {themeLabel(t)}
              </button>
            );
          })}
        </div>
      </section>

      {/* Key2 — voice */}
      <section className="card">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">Key2 — Voice AI</h2>
          <span className={`badge ${status?.liveVoiceEnabled ? 'badge-success' : ''}`}>
            {status?.liveVoiceEnabled ? 'Enabled' : 'Optional'}
          </span>
        </div>
        <p className="mt-1 text-sm text-fg-muted">
          {status?.liveVoiceEnabled
            ? 'Live voice chat is enabled.'
            : 'Add a realtime voice model to enable live voice chat.'}
        </p>
        <form className="mt-4 space-y-4" onSubmit={saveKey2}>
          <label className="block">
            <span className="field-label">Voice provider</span>
            <select
              className="select"
              value={k2provider}
              onChange={(e) => setK2provider(e.target.value)}
              aria-label="Voice provider"
            >
              <option value="openai_realtime">OpenAI Realtime</option>
              <option value="azure_foundry">Azure AI Foundry</option>
            </select>
          </label>
          {isAzure ? (
            <label className="block">
              <span className="field-label">Azure endpoint</span>
              <input
                className="input"
                value={k2endpoint}
                onChange={(e) => setK2endpoint(e.target.value)}
                placeholder="https://your-resource.openai.azure.com"
                aria-label="Azure endpoint"
                required
              />
              <span className="field-hint">Your Azure AI Foundry resource endpoint.</span>
            </label>
          ) : null}
          <label className="block">
            <span className="field-label">{isAzure ? 'Deployment name' : 'Voice model'}</span>
            <input
              className="input"
              value={k2model}
              onChange={(e) => setK2model(e.target.value)}
              placeholder="gpt-realtime"
              aria-label="Voice model"
              required
            />
          </label>
          <label className="block">
            <span className="field-label">API key</span>
            <input
              className="input"
              type="password"
              autoComplete="off"
              value={k2key}
              onChange={(e) => setK2key(e.target.value)}
              placeholder="Paste your key"
              aria-label="Voice API key"
              required
            />
          </label>
          <button type="submit" className="btn" disabled={busy}>
            Save Key2
          </button>
        </form>
      </section>

      {/* Data */}
      <section className="card">
        <h2 className="text-base font-semibold">Your data</h2>
        <p className="mt-1 text-sm text-fg-muted">
          Export a portable copy or review recent access.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void doExport('all')()}
            disabled={busy}
          >
            Export all
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void doExport('persona')()}
            disabled={busy}
          >
            Export persona
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void doExport('corpus')()}
            disabled={busy}
          >
            Export corpus
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void loadAudit()}
            disabled={busy}
          >
            View audit log
          </button>
        </div>
        {audit ? (
          <ul
            className="mt-4 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-line/10 bg-surface-2 p-3 text-sm"
            data-testid="audit-list"
          >
            {audit.map((a, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-fg-muted">
                <span className="font-medium text-fg">{String(a.action)}</span>
                <span className="text-xs text-fg-subtle">{String(a.at ?? '')}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {/* Danger zone */}
      <section className="card border-danger/25">
        <h2 className="text-base font-semibold text-danger">Danger zone</h2>
        <p className="mt-1 text-sm text-fg-muted">
          Irreversible actions. Export first if you might want your data back.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => setConfirming('persona')}
            disabled={busy}
          >
            Delete persona
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => setConfirming('all')}
            disabled={busy}
          >
            Kill switch — delete everything
          </button>
        </div>
      </section>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming === 'all' ? 'Delete everything?' : 'Delete persona?'}
        message={
          confirming === 'all'
            ? 'This permanently destroys your Corpus and Persona. This cannot be undone.'
            : 'Removes your distilled persona. Your Corpus is kept, so it can be rebuilt.'
        }
        confirmLabel={confirming === 'all' ? 'Delete everything' : 'Delete persona'}
        danger
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void runDelete(confirming === 'all' ? 'all' : 'persona')()}
      />
    </div>
  );
}
